import { useState, useMemo } from "react";
import { Icon } from "@iconify/react";
import { formatDate } from "../../utils/formatters";
import Button from "../common/Button";
import LoadingSpinner from "../common/LoadingSpinner";

// Calculate section score based on passes and failures
const calculateSectionScore = (items, type) => {
  if (!items || items.length === 0) return 100;

  if (type === "violations") {
    // For violations, score decreases based on count and severity
    let penalty = 0;
    items.forEach((item) => {
      const impact = item.impact?.toLowerCase();
      const nodeCount = item.nodes?.length || 1;
      if (impact === "critical") penalty += 15 * nodeCount;
      else if (impact === "serious") penalty += 10 * nodeCount;
      else if (impact === "moderate") penalty += 5 * nodeCount;
      else penalty += 2 * nodeCount;
    });
    return Math.max(0, 100 - penalty);
  }
  return 100;
};

// Status Icon Component
const StatusIcon = ({ type, score }) => {
  if (type === "pass" || score === 100) {
    return (
      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
        <Icon icon="mdi:check" className="text-green-600 text-sm" />
      </div>
    );
  }
  if (type === "fail" || score === 0) {
    return (
      <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
        <Icon icon="mdi:close" className="text-red-600 text-sm" />
      </div>
    );
  }
  return (
    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
      <div className="w-3 h-3 rounded-full border-2 border-gray-500"></div>
    </div>
  );
};

// Score Badge Component
const ScoreBadge = ({ score }) => {
  const getBadgeColor = () => {
    if (score >= 90) return "bg-green-100 text-green-700";
    if (score >= 70) return "bg-yellow-100 text-yellow-700";
    return "bg-red-100 text-red-700";
  };

  return (
    <span
      className={`px-3 py-1 rounded text-sm font-medium ${getBadgeColor()}`}
    >
      Score: {score}
    </span>
  );
};

// Progress Bar Component
const ProgressBar = ({ label, value, total, type }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  const barColor = type === "failures" ? "bg-red-500" : "bg-green-500";

  return (
    <div className="mb-3">
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm text-gray-600">{label}</span>
        <span className="text-sm font-medium text-gray-800">{value}</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} rounded-full transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    </div>
  );
};

const IMPACT_STYLES = {
  critical: "bg-red-100 text-red-700 border-red-200",
  serious: "bg-orange-100 text-orange-700 border-orange-200",
  moderate: "bg-yellow-100 text-yellow-700 border-yellow-200",
  minor: "bg-blue-100 text-blue-700 border-blue-200",
};

// Single Result Item Component
const ResultItem = ({ item, type }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const nodes = item.nodes || [];
  const failureCount = type === "violation" ? nodes.length : 0;
  const successCount = type === "pass" ? nodes.length : 0;
  const totalElements = nodes.length;

  const getStatusType = () => {
    if (type === "pass") return "pass";
    if (type === "violation") return "fail";
    return "neutral";
  };

  const impactKey = item.impact?.toLowerCase();
  const impactStyle = IMPACT_STYLES[impactKey];

  return (
    <div className="border-b border-gray-200 last:border-b-0">
      {/* Accordion Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-3 py-3 px-4 hover:bg-gray-50 transition-colors text-left"
        aria-expanded={isExpanded}
      >
        <StatusIcon type={getStatusType()} />

        <span className="flex-1 text-sm text-gray-800 font-medium">
          {item.help || item.id}
        </span>

        {impactStyle && (
          <span
            className={`text-[10px] font-bold px-2 py-0.5 rounded border capitalize shrink-0 ${impactStyle}`}
          >
            {impactKey}
          </span>
        )}

        <Icon
          icon={isExpanded ? "mdi:chevron-up" : "mdi:chevron-down"}
          className="text-gray-400 text-xl flex-shrink-0"
        />
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="bg-gray-50 border-t border-gray-200">
          <div className="flex flex-col lg:flex-row">
            {/* Left Column - Main Content */}
            <div className="flex-1 p-4 border-b lg:border-b-0 lg:border-r border-gray-200">
              {/* Requirement */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">
                  Requirement:
                </h4>
                <p className="text-sm text-gray-600">{item.description}</p>
              </div>

              {/* Code Snapshots */}
              {nodes.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">
                    {nodes.length} Code snapshot{nodes.length > 1 ? "s" : ""} of{" "}
                    {type === "violation" ? "failed" : "successful"} elements
                  </h4>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {nodes.slice(0, 5).map((node, nodeIdx) => (
                      <div key={nodeIdx} className="flex items-start gap-2">
                        <span
                          className={`flex-shrink-0 w-6 h-6 rounded flex items-center justify-center text-xs font-medium text-white ${type === "violation" ? "bg-red-500" : "bg-green-500"}`}
                        >
                          {nodeIdx + 1}
                        </span>
                        <div className="flex-1 space-y-1.5 min-w-0">
                          {/* Selector */}
                          {node.selector && (
                            <p className="text-[10px] text-gray-400 font-mono">
                              selector:{" "}
                              <span className="text-gray-600">
                                {node.selector}
                              </span>
                            </p>
                          )}
                          {/* Current HTML */}
                          <code className="text-xs bg-gray-800 text-gray-100 p-2 rounded overflow-x-auto block whitespace-pre-wrap break-all">
                            {node.html}
                          </code>
                          {/* Failure Summary */}
                          {node.failureSummary && (
                            <p className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1 whitespace-pre-line">
                              {node.failureSummary}
                            </p>
                          )}
                          {/* Suggested Fix */}
                          {node.suggestedFix && (
                            <div>
                              <p className="text-[10px] font-semibold text-green-700 uppercase tracking-wide mb-0.5">
                                Suggested Fix
                              </p>
                              <code className="text-xs bg-green-900 text-green-100 p-2 rounded overflow-x-auto block whitespace-pre-wrap break-all">
                                {node.suggestedFix}
                              </code>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {nodes.length > 5 && (
                      <p className="text-xs text-gray-500 ml-8">
                        + {nodes.length - 5} more elements
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Help URL */}
              {item.helpUrl && (
                <a
                  href={item.helpUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline text-sm flex items-center gap-1"
                >
                  Learn more about this requirement
                  <Icon icon="mdi:chevron-right" />
                </a>
              )}
            </div>

            {/* Right Column - Stats */}
            <div className="w-full lg:w-72 p-4 bg-white">
              {/* Total Elements */}
              {totalElements > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-3">
                    Total elements on page
                  </h4>
                  <ProgressBar
                    label="Failures"
                    value={failureCount}
                    total={totalElements}
                    type="failures"
                  />
                  <ProgressBar
                    label="Successes"
                    value={successCount}
                    total={totalElements}
                    type="successes"
                  />
                </div>
              )}

              {/* Neutral Score Message for inapplicable/incomplete */}
              {type === "inapplicable" || type === "incomplete" ? (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">
                    Neutral Score:
                  </h4>
                  <p className="text-sm text-gray-600">
                    These elements are either missing or irrelevant, making the
                    requirement not applicable.
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Applied Fix Item Component
const AppliedFixItem = ({ fix, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const impactKey = fix.impact?.toLowerCase();
  const impactStyle = IMPACT_STYLES[impactKey];

  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-3 py-3 px-4 hover:bg-gray-50 transition-colors text-left"
        aria-expanded={isExpanded}
      >
        <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
          <Icon icon="mdi:wrench-check" className="text-teal-600 text-sm" />
        </div>

        <span className="flex-1 text-sm text-gray-800 font-medium min-w-0 truncate">
          {fix.violationId || fix.rule || `Fix #${index + 1}`}
        </span>

        {impactStyle && (
          <span
            className={`text-[10px] font-bold px-2 py-0.5 rounded border capitalize shrink-0 ${impactStyle}`}
          >
            {impactKey}
          </span>
        )}

        <Icon
          icon={isExpanded ? "mdi:chevron-up" : "mdi:chevron-down"}
          className="text-gray-400 text-xl flex-shrink-0"
        />
      </button>

      {isExpanded && (
        <div className="bg-gray-50 border-t border-gray-200 p-4">
          {fix.nestedSelector && (
            <p className="text-[10px] text-gray-400 font-mono mb-3 break-all">
              {fix.nestedSelector}
            </p>
          )}

          {(fix.originalHtml || fix.fixedHtml) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {fix.originalHtml && (
                <div>
                  <p className="text-[10px] font-semibold text-red-600 mb-1 flex items-center gap-1">
                    <Icon icon="mdi:close-circle-outline" />
                    Before
                  </p>
                  <code className="text-xs bg-red-50 border border-red-100 text-red-900 p-2 rounded block overflow-x-auto whitespace-pre-wrap break-all">
                    {fix.originalHtml}
                  </code>
                </div>
              )}
              {fix.fixedHtml && (
                <div>
                  <p className="text-[10px] font-semibold text-green-600 mb-1 flex items-center gap-1">
                    <Icon icon="mdi:check-circle-outline" />
                    After
                  </p>
                  <code className="text-xs bg-green-50 border border-green-100 text-green-900 p-2 rounded block overflow-x-auto whitespace-pre-wrap break-all">
                    {fix.fixedHtml}
                  </code>
                </div>
              )}
            </div>
          )}

          {fix.target && Array.isArray(fix.target) && (
            <p className="text-[10px] text-gray-500 mt-3 font-mono">
              target: {fix.target.join(" > ")}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// Category Section Component
const CategorySection = ({ category, items, type }) => {
  const sectionScore = calculateSectionScore(items, type);

  return (
    <div className="bg-white rounded-lg border border-gray-200 mb-4 overflow-hidden">
      {/* Category Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800">{category}</h3>
        <ScoreBadge score={sectionScore} />
      </div>

      {/* Items */}
      {items.map((item, index) => (
        <ResultItem
          key={`${item.id}-${index}`}
          item={item}
          type={
            type === "violations"
              ? "violation"
              : type === "passes"
                ? "pass"
                : "neutral"
          }
        />
      ))}
    </div>
  );
};

const ScanResults = ({
  scan,
  onApplyFixes,
  isApplyingFixes,
  onExportReport,
  shopifyStatus,
  isCheckingShopifyStatus,
  onEnableShopifyExtension,
}) => {
  if (!scan) return null;

  const [activeTab, setActiveTab] = useState("violations");

  const violations = scan.violations || [];
  const passes = scan.passes || [];
  const incomplete = scan.incomplete || [];
  const inapplicable = scan.inapplicable || [];
  const manualPasses = scan.manualPasses || [];
  const manualNotApplicable = scan.manualNotApplicable || [];
  const appliedFixesRaw = scan.appliedFixes;
  const appliedFixesList = Array.isArray(appliedFixesRaw)
    ? appliedFixesRaw
    : appliedFixesRaw?.fixes || [];
  const appliedFixesAt =
    appliedFixesRaw?.appliedAt || appliedFixesRaw?.applied_at || null;
  const summary = scan.summary || {};
  const totalViolations = summary.totalViolations ?? violations.length;
  const totalIssues = summary.totalIssues ?? null;
  const totalPasses = summary.totalPasses ?? passes.length;
  const totalIncomplete = summary.totalIncomplete ?? incomplete.length;
  const totalInapplicable = summary.totalInapplicable ?? inapplicable.length;
  const totalManualPasses = manualPasses.length;
  const totalManualNotApplicable = manualNotApplicable.length;
  const totalAppliedFixes = appliedFixesList.length;
  const complianceScore =
    summary.complianceScore ??
    (violations.length === 0 ? 100 : Math.max(0, 100 - violations.length * 5));

  // Compute impact counts from violations if not provided by API
  const violationsByImpact = useMemo(() => {
    if (summary.violationsByImpact) return summary.violationsByImpact;
    const counts = { critical: 0, serious: 0, moderate: 0, minor: 0 };
    violations.forEach((v) => {
      const key = v.impact?.toLowerCase();
      if (key in counts) counts[key]++;
    });
    return counts;
  }, [violations, summary.violationsByImpact]);

  // Group items by section/category
  const groupBySection = (items) => {
    const groups = {};
    items.forEach((item) => {
      const section = item.section || "General";
      if (!groups[section]) groups[section] = [];
      groups[section].push(item);
    });
    return groups;
  };

  const violationsBySection = useMemo(
    () => groupBySection(violations),
    [violations],
  );
  const passesBySection = useMemo(() => groupBySection(passes), [passes]);
  const incompleteBySection = useMemo(
    () => groupBySection(incomplete),
    [incomplete],
  );
  const inapplicableBySection = useMemo(
    () => groupBySection(inapplicable),
    [inapplicable],
  );
  const manualPassesBySection = useMemo(
    () => groupBySection(manualPasses),
    [manualPasses],
  );
  const manualNotApplicableBySection = useMemo(
    () => groupBySection(manualNotApplicable),
    [manualNotApplicable],
  );

  const renderCategorySections = (groupedItems, type) => {
    const sections = Object.entries(groupedItems);
    if (sections.length === 0) {
      return (
        <div className="text-center py-8">
          <Icon
            icon="mdi:check-circle"
            className="text-4xl sm:text-5xl text-success mx-auto mb-2"
          />
          <p className="text-sm sm:text-base text-gray-600">
            {type === "violations"
              ? "No accessibility violations found!"
              : `No ${type} recorded.`}
          </p>
        </div>
      );
    }
    return sections.map(([section, items]) => (
      <CategorySection
        key={section}
        category={section}
        items={items}
        type={type}
      />
    ));
  };

  const getScoreRingColor = () => {
    if (complianceScore >= 90) return "text-green-500";
    if (complianceScore >= 70) return "text-yellow-500";
    return "text-red-500";
  };

  const getScoreBgColor = () => {
    if (complianceScore >= 90) return "bg-green-50 border-green-200";
    if (complianceScore >= 70) return "bg-yellow-50 border-yellow-200";
    return "bg-red-50 border-red-200";
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 mb-6 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Icon
              icon="mdi:clipboard-check"
              className="text-gray-600 text-xl"
            />
            <h2 className="text-lg font-semibold text-gray-800">
              Scan Results
            </h2>
          </div>
          {scan.scanStatus &&
            (() => {
              const statusMap = {
                scan_completed: {
                  label: "Scan Completed",
                  icon: "mdi:check-circle",
                  cls: "bg-green-100 text-green-700 border-green-200",
                },
                fixes_applied: {
                  label: "Fixes Applied",
                  icon: "mdi:wrench-check",
                  cls: "bg-teal-100 text-teal-700 border-teal-200",
                },
                applying_fixes: {
                  label: "Applying Fixes",
                  icon: "mdi:auto-fix",
                  cls: "bg-orange-100 text-orange-700 border-orange-200",
                },
                in_progress: {
                  label: "In Progress",
                  icon: "mdi:progress-clock",
                  cls: "bg-blue-100 text-blue-700 border-blue-200",
                },
                failed: {
                  label: "Failed",
                  icon: "mdi:alert-circle",
                  cls: "bg-red-100 text-red-700 border-red-200",
                },
              };
              const s = statusMap[scan.scanStatus];
              return s ? (
                <span
                  className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${s.cls}`}
                >
                  <Icon icon={s.icon} />
                  {s.label}
                </span>
              ) : null;
            })()}
        </div>
      </div>

      {/* Summary Section */}
      <div className="p-4">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left: Score Circle */}
          <div
            className={`flex-shrink-0 p-6 rounded-xl border ${getScoreBgColor()} flex flex-col items-center justify-center min-w-[180px]`}
          >
            <div className="relative w-28 h-28">
              <svg
                className="w-28 h-28 transform -rotate-90"
                viewBox="0 0 100 100"
              >
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="8"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  className={getScoreRingColor()}
                  stroke="currentColor"
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${complianceScore * 2.83} 283`}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-3xl font-bold ${getScoreRingColor()}`}>
                  {complianceScore}%
                </span>
              </div>
            </div>
            <p className="mt-2 text-sm font-medium text-gray-600">
              Compliance Score
            </p>
          </div>

          {/* Right: Details */}
          <div className="flex-1 min-w-0">
            {/* URL */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon icon="mdi:web" className="text-gray-400 text-sm" />
                <span className="text-xs text-gray-500 uppercase tracking-wide">
                  Scanned URL
                </span>
              </div>
              <p className="text-sm font-medium text-gray-800 break-all">
                {scan.url}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {formatDate(scan.timestamp || scan.scanDate || scan.createdAt)}
              </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {totalIssues !== null && (
                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <p className="text-2xl font-bold text-orange-600">
                    {totalIssues}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Total Issues</p>
                </div>
              )}
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <p
                  className={`text-2xl font-bold ${totalViolations > 0 ? "text-red-600" : "text-gray-400"}`}
                >
                  {totalViolations}
                </p>
                <p className="text-xs text-gray-500 mt-1">Violations</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">
                  {totalPasses}
                </p>
                <p className="text-xs text-gray-500 mt-1">Passes</p>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">
                  {totalIncomplete}
                </p>
                <p className="text-xs text-gray-500 mt-1">Incomplete</p>
              </div>
              <div className="text-center p-3 bg-gray-100 rounded-lg">
                <p className="text-2xl font-bold text-gray-600">
                  {totalInapplicable}
                </p>
                <p className="text-xs text-gray-500 mt-1">N/A</p>
              </div>
            </div>

            {/* Impact Breakdown - Only show if there are violations */}
            {totalViolations > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {violationsByImpact?.critical > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                    {violationsByImpact.critical} Critical
                  </span>
                )}
                {violationsByImpact?.serious > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                    <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
                    {violationsByImpact.serious} Serious
                  </span>
                )}
                {violationsByImpact?.moderate > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                    <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full"></span>
                    {violationsByImpact.moderate} Moderate
                  </span>
                )}
                {violationsByImpact?.minor > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                    {violationsByImpact.minor} Minor
                  </span>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={onExportReport}
                className="flex items-center gap-1.5"
              >
                <Icon icon="mdi:download" />
                <span>Export</span>
              </Button>

              {isCheckingShopifyStatus && (
                <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                  <LoadingSpinner size="sm" />
                  Checking Shopify AI Fixes status...
                </span>
              )}

              {!isCheckingShopifyStatus && shopifyStatus && (
                <>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                      shopifyStatus.enabled
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    <Icon
                      icon={
                        shopifyStatus.enabled
                          ? "mdi:check-circle"
                          : "mdi:close-circle-outline"
                      }
                    />
                    AI Fixes {shopifyStatus.enabled ? "Enabled" : "Disabled"}
                  </span>

                  <Button
                    variant="primary"
                    size="sm"
                    disabled={!shopifyStatus.enabled}
                    className="flex items-center gap-1.5"
                  >
                    <Icon icon="mdi:auto-fix" />
                    <span>AI Fixes</span>
                  </Button>

                  {!shopifyStatus.enabled && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={onEnableShopifyExtension}
                      className="flex items-center gap-1.5"
                    >
                      <Icon icon="logos:shopify" />
                      <span>Enable in Shopify</span>
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Success Message */}
        {totalViolations === 0 && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
            <Icon icon="mdi:check-circle" className="text-green-600 text-lg" />
            <p className="text-green-700 font-medium text-sm">
              No accessibility violations found! Your page is compliant.
            </p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex flex-wrap gap-1 overflow-x-auto">
          <button
            onClick={() => setActiveTab("violations")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "violations"
                ? "border-red-500 text-red-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Violations ({totalViolations})
          </button>
          <button
            onClick={() => setActiveTab("passes")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "passes"
                ? "border-green-500 text-green-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Passes ({totalPasses})
          </button>
          {totalIncomplete > 0 && (
            <button
              onClick={() => setActiveTab("incomplete")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "incomplete"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Incomplete ({totalIncomplete})
            </button>
          )}
          {totalInapplicable > 0 && (
            <button
              onClick={() => setActiveTab("inapplicable")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "inapplicable"
                  ? "border-gray-500 text-gray-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Inapplicable ({totalInapplicable})
            </button>
          )}
          {totalManualPasses > 0 && (
            <button
              onClick={() => setActiveTab("manualPasses")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "manualPasses"
                  ? "border-green-500 text-green-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Manual Passes ({totalManualPasses})
            </button>
          )}
          {totalManualNotApplicable > 0 && (
            <button
              onClick={() => setActiveTab("manualNotApplicable")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "manualNotApplicable"
                  ? "border-gray-500 text-gray-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Manual N/A ({totalManualNotApplicable})
            </button>
          )}
          <button
            onClick={() => setActiveTab("appliedFixes")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "appliedFixes"
                ? "border-teal-500 text-teal-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Applied Fixes ({totalAppliedFixes})
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-4 p-4">
        {activeTab === "violations" &&
          renderCategorySections(violationsBySection, "violations")}
        {activeTab === "passes" &&
          renderCategorySections(passesBySection, "passes")}
        {activeTab === "incomplete" &&
          renderCategorySections(incompleteBySection, "incomplete")}
        {activeTab === "inapplicable" &&
          renderCategorySections(inapplicableBySection, "inapplicable")}
        {activeTab === "manualPasses" &&
          renderCategorySections(manualPassesBySection, "manualPasses")}
        {activeTab === "manualNotApplicable" &&
          renderCategorySections(
            manualNotApplicableBySection,
            "manualNotApplicable",
          )}
        {activeTab === "appliedFixes" && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {appliedFixesAt && (
              <div className="px-4 py-3 bg-teal-50 border-b border-teal-100 flex items-center gap-2">
                <Icon icon="mdi:clock-check-outline" className="text-teal-600" />
                <p className="text-sm text-teal-800">
                  Fixes applied on{" "}
                  <span className="font-medium">{formatDate(appliedFixesAt)}</span>
                </p>
              </div>
            )}
            {totalAppliedFixes > 0 ? (
              appliedFixesList.map((fix, index) => (
                <AppliedFixItem key={fix.nestedSelector || fix.violationId || index} fix={fix} index={index} />
              ))
            ) : (
              <div className="text-center py-8">
                <Icon
                  icon="mdi:wrench-outline"
                  className="text-4xl sm:text-5xl text-gray-300 mx-auto mb-2"
                />
                <p className="text-sm sm:text-base text-gray-600">
                  No fixes have been applied for this scan yet.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScanResults;
